import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Button, Tabs, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import type { PopoverMenuTouchableOptionProps as PopoverOption } from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import NewSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/NewSettingVersionModal'
import DeleteModal from 'modules/attendance/components/SettingItemPage/TimeTracking/PolicyActions/DeleteModal'
import SettingsPanel from 'modules/attendance/components/SettingItemPage/TimeTracking/SettingsPanel'
// TODO: mix of private components from parent component
import { TabTypes, buildTabs } from 'modules/attendance/lib/tabs'
import attendanceSettingsCollection, {
  attendanceSettingVersionIndex,
  isCurrentAttendanceSetting,
} from 'modules/attendance/store/collections/settings'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import TagComponent from 'modules/layout/components/Page/Header/_Tag/'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import Content from './Content'
import useFetchData from './useFetchData'

interface Props {
  settingId: string | 'default'
  tabId?: TabTypes
}

function TimeTrackingPolicy({ settingId, tabId }: Props) {
  const { loading, attendancePolicy, currentSetting, attendanceSettings } = useFetchData(settingId)
  const { default: defaultPolicy, name: policyName } = attendancePolicy?.attributes ?? {}
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const [settingsPanelOpen, setSettingsPanelOpen] = React.useState(false)
  const [newSettingVersionModalOpen, setNewSettingVersionModalOpen] = React.useState(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const tabs = currentSetting ? buildTabs(currentSetting, navigation) : []
  const loaded = !loading && !!attendancePolicy && !!currentSetting && !!attendanceSettings
  const isOldVersion =
    loaded && !(isCurrentAttendanceSetting(currentSetting) || currentSetting.future)
  const [openCallout, setOpenCallout] = React.useState<boolean>(isOldVersion)
  const currentPathname = !!tabId && pathname.includes(tabId) ? pathname : `${pathname}/${tabId}`

  const submitSetAsDefault = async () => {
    try {
      await attendancePolicy?.rpc('set_as_default')
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('time_tracking.settings.error_set_as_default'),
      })
    }

    app.setFlash({
      type: 'correct',
      message: i18n.t('time_tracking.settings.success_set_as_default'),
    })
  }

  const refreshPage = (setting: AttendanceSetting) => {
    const routeParams = {
      id: 'time_tracking',
      settingId: setting.attributes.id.toString(),
      tabId: tabId || 'edit',
    }

    return navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onChangeSetting = (newSetting: AttendanceSetting) => {
    setSettingsPanelOpen(false)
    refreshPage(newSetting)
  }

  const onResponseSettingVersionModal = (newSetting: AttendanceSetting) => {
    setNewSettingVersionModalOpen(false)
    attendanceSettingsCollection.add(newSetting)
    refreshPage(newSetting)
  }

  const options = () => {
    if (!attendancePolicy || defaultPolicy) return

    return [
      {
        title: i18n.t('time_tracking.settings.delete'),
        onClick: () => setDeleteModalOpen(true),
        danger: true,
      },
    ]
  }

  const popoverOptions: PopoverOption[] = [
    {
      title: i18n.t('time_tracking.settings.versions.popover_options.add'),
      description: undefined,
      onClick: () => setNewSettingVersionModalOpen(true),
    },
    {
      title: i18n.t('time_tracking.settings.versions.popover_options.show'),
      description: i18n.t('time_tracking.settings.versions.popover_options.show_description'),
      onClick: () => setSettingsPanelOpen(true),
    },
  ]

  return (
    <ResponsivePage.Body showHeader={false} actions={{ secondary: options() }} variant="full">
      {openCallout && (
        <Box paddingBottom="s24">
          <Callout
            type="warning"
            title=""
            description={i18n.t('time_tracking.settings.versions.notices.old_version_message', {
              policy_name: policyName,
            })}
            compact
            onRemove={() => setOpenCallout(false)}
          />
        </Box>
      )}
      <Box justifyContent="spaceBetween" flexDirection="row" paddingBottom="s32">
        <Text size="500" weight="semibold">
          {policyName}
        </Text>
        <Box flexDirection="row" justifyContent="spaceBetween" gap="s8">
          <TagComponent
            label={
              currentSetting
                ? i18n.t('time_tracking.settings.versions.version_with_index', {
                    index: attendanceSettingVersionIndex(currentSetting),
                  })
                : ''
            }
            type={Tag.Types.grey4}
            options={popoverOptions}
          />
          {!loading && !defaultPolicy && (
            <Button type="negative" iconName="star" onClick={submitSetAsDefault}>
              {i18n.t('time_tracking.settings.set_as_default')}
            </Button>
          )}
        </Box>
      </Box>
      <Tabs
        currentId={currentPathname}
        onTabClicked={(tabId) => {
          tabs.find((tab) => tab.href === tabId)?.onClick?.()
          navigate(tabId)
        }}
      >
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.href} id={tab.href} label={tab.name} />
        ))}
      </Tabs>
      <Content
        tabId={tabId}
        attendancePolicy={attendancePolicy}
        attendanceSetting={currentSetting}
      />
      {deleteModalOpen && attendancePolicy && (
        <DeleteModal
          attendancePolicy={attendancePolicy}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
      {loaded && newSettingVersionModalOpen && (
        <NewSettingVersionModal
          attendancePolicy={attendancePolicy}
          attendanceSetting={currentSetting}
          attendanceSettings={attendanceSettings}
          onClose={() => setNewSettingVersionModalOpen(false)}
          onResponse={onResponseSettingVersionModal}
        />
      )}
      {loaded && (
        <SettingsPanel
          selectedSetting={currentSetting}
          attendanceSettings={attendanceSettings}
          open={settingsPanelOpen}
          onChange={onChangeSetting}
          onClose={() => setSettingsPanelOpen(false)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default observer(TimeTrackingPolicy)
